import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Loader,
  Ban,
  ChevronRight,
  Clock,
  History,
  AlertTriangle,
} from 'lucide-react';
import { LoadingSpinner } from '../common';
import { getGradeFromScore } from '../analysis/scores';
import { getGradeStyles, countGaps } from './utils';
import { Pagination } from './Pagination';
import type { AnalysisJob, PillarScore } from './types';

const PAGE_SIZE = 10;

interface AnalysisHistoryProps {
  history: AnalysisJob[];
  loading: boolean;
  error?: string | null;
  startingAnalysis: boolean;
  isUpload: boolean;
  onStartAnalysis: () => void;
  onRetry?: () => void;
}

export const AnalysisHistory: React.FC<AnalysisHistoryProps> = ({
  history,
  loading,
  error,
  startingAnalysis,
  isUpload,
  onStartAnalysis,
  onRetry,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Load failed: keep this distinct from the empty state so prior analyses
  // don't look permanently gone when the request merely timed out.
  if (error && history.length === 0) {
    return (
      <section>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
            <History className="w-5 h-5 text-teal-600 dark:text-teal-400" />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Analysis History
          </h2>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700/50 rounded-xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Couldn't load analysis history
          </h3>
          <p className="mb-6 max-w-sm mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
            Something went wrong loading your previous analyses. They haven't been lost. Please try
            again in a moment.
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg"
            >
              Try again
            </button>
          )}
        </div>
      </section>
    );
  }

  // Empty state
  if (history.length === 0) {
    return (
      <section>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
            <History className="w-5 h-5 text-teal-600 dark:text-teal-400" />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Analysis History
          </h2>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700/50 rounded-xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
            <Clock className="w-8 h-8 text-teal-600 dark:text-teal-400" />
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            No Analysis Yet
          </h3>
          <p className="mb-6 max-w-sm mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
            Run your first analysis to discover insights, gaps, and recommendations for this
            repository.
          </p>
          <button
            onClick={onStartAnalysis}
            disabled={startingAnalysis}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 rounded-lg"
          >
            {startingAnalysis ? (
              <>
                <LoadingSpinner size="sm" />
                Starting...
              </>
            ) : (
              <>
                <Clock className="w-4 h-4" />
                {isUpload ? 'Upload & Analyze' : 'Run Analysis'}
              </>
            )}
          </button>
        </div>
      </section>
    );
  }

  const totalPages = Math.ceil(history.length / PAGE_SIZE);
  const visible = history.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
          <History className="w-5 h-5 text-teal-600 dark:text-teal-400" />
        </div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Analysis History
        </h2>
      </div>
      <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700/50 rounded-xl divide-y divide-slate-200 dark:divide-slate-700/50">
        {visible.map((job: AnalysisJob) => {
          const isCompleted = job.status?.toLowerCase() === 'completed';
          const score = job.analysis?.score;
          const jobGradeStyles = score !== undefined ? getGradeStyles(score) : null;
          const maintenanceScore = job.analysis?.maintenanceScore?.overallScore;
          const depPillar = job.analysis?.maintenanceScore?.pillarScores?.find(
            (p: PillarScore) => p.name === 'dependency' || p.name === 'dependencies',
          );

          return (
            <a
              key={job.jobId}
              href={`/jobs/${job.jobId}${isCompleted ? '/results' : ''}`}
              className="w-full text-left p-4 first:rounded-t-xl last:rounded-b-xl block border-l-2 border-l-transparent hover:border-l-teal-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {job.status?.toLowerCase() === 'completed' && (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  )}
                  {job.status?.toLowerCase() === 'failed' && (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  {job.status?.toLowerCase() === 'cancelled' && (
                    <Ban className="w-5 h-5 text-gray-400" />
                  )}
                  {(job.status?.toLowerCase() === 'running' ||
                    job.status?.toLowerCase() === 'pending') && (
                    <Loader className="w-5 h-5 text-blue-500" />
                  )}

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {job.status?.toLowerCase() === 'completed' && 'Completed'}
                        {job.status?.toLowerCase() === 'failed' && 'Failed'}
                        {job.status?.toLowerCase() === 'cancelled' && 'Cancelled'}
                        {job.status?.toLowerCase() === 'running' && 'Running'}
                        {job.status?.toLowerCase() === 'pending' && 'Pending'}
                      </span>
                      {isCompleted && jobGradeStyles && (
                        <span
                          className={`px-2 py-0.5 text-xs font-semibold rounded ${jobGradeStyles.bg} ${jobGradeStyles.text}`}
                        >
                          SDLC {getGradeFromScore(score ?? 0)} · {score}
                        </span>
                      )}
                      {isCompleted && maintenanceScore !== undefined && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                          Maintenance {maintenanceScore}
                        </span>
                      )}
                      {isCompleted && depPillar && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                          Drift {depPillar.score}
                        </span>
                      )}
                    </div>
                    {isCompleted && job.analysis && (
                      <p className="text-sm mt-0.5" style={{ color: 'var(--color-text-tertiary)' }}>
                        {countGaps(job.analysis.gaps)} gaps ·{' '}
                        {job.analysis.recommendationsCount ??
                          job.analysis.recommendations?.length ??
                          0}{' '}
                        findings
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                    {job.startedAt &&
                      new Date(job.startedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                  </span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </a>
          );
        })}
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={history.length}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />
    </section>
  );
};
